// One-shot "Install NetTV on your iPhone" banner for Safari users.
// Shows up exactly once per device. Dismissed version stays in localStorage.
// Detects iOS Safari reliably and skips when already installed as PWA.
import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "nettv.ios_install_dismissed";

function isIosSafari() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  // Already installed = don't nag
  const standalone =
    window.navigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches;
  return isIos && isSafari && !standalone;
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIosSafari()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    // Delay a beat so it doesn't collide with disclaimer modal
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <div
      data-testid="ios-install-prompt"
      className="fixed bottom-4 left-4 right-4 z-[9998] rounded-2xl border border-cyan-400/30 bg-[#0f1624]/95 backdrop-blur-md shadow-2xl p-4 max-w-md mx-auto"
    >
      <div className="flex items-start gap-3">
        <img
          src="/icons/apple-touch-icon-180.png"
          alt="NetTV"
          className="w-12 h-12 rounded-xl flex-shrink-0"
          loading="eager"
        />
        <div className="flex-1">
          <div className="font-semibold text-white text-sm">Install NetTV on your iPhone</div>
          <div className="text-xs text-white/70 mt-1 leading-relaxed">
            Tap <Share className="w-3.5 h-3.5 inline align-text-bottom" /> in Safari, then
            <span className="inline-flex items-center gap-1 mx-1">
              <Plus className="w-3 h-3" /> <strong>Add to Home Screen</strong>.
            </span>
            Fullscreen, no browser bars.
          </div>
        </div>
        <button
          data-testid="ios-install-dismiss"
          onClick={dismiss}
          className="p-1.5 text-white/60 hover:text-white rounded-lg hover:bg-white/10"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
